import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { LoadingComponent } from 'recupero-comunes';
import { FaSearch } from 'react-icons/fa';
import { BsFileEarmarkMedicalFill } from 'react-icons/bs';
import { serviceGetInformePenal } from '../../api/api-informe-penal';
import InformePenalLectura from './InformePenalLectura';

const estiloEncabezado = { color: 'white', backgroundColor: '#7594C1' };

// Estado del informe como insignia de color (prototipo): Enviado turquesa, Revisado morado, Borrador amarillo. Todo en línea (sin las clases
// de badge de Bootstrap) para que el tema global de la app no cambie el color.
const COLORES_ESTADO = {
    355001: { fondo: '#d4a842', texto: '#4a3c00' },
    355002: { fondo: '#5aacb6', texto: '#FFFFFF' },
    355003: { fondo: '#4F517A', texto: '#FFFFFF' }
};

const InsigniaEstado = ({ estado, glosa }) => {
    const color = COLORES_ESTADO[estado] || { fondo: '#6c757d', texto: '#FFFFFF' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 700,
            backgroundColor: color.fondo, color: color.texto
        }}>
            {glosa}
        </span>
    );
};

const DatoCaso = ({ etiqueta, valor }) => (
    <div>
        <div style={{ fontWeight: 700, color: '#333' }}>{etiqueta}</div>
        <div style={{ color: '#666' }}>{valor === null || valor === undefined || valor === '' ? '—' : valor}</div>
    </div>
);

const codifica = (datos) => Buffer.from(JSON.stringify(datos)).toString('base64');

// «Historial Informe Defensa Penal» (RG-576): grilla de los informes del caso (el abogado ve todos, incluido el
// borrador; el analista no) y detalle en solo lectura con los atributos modificados. Lo usan el abogado y el analista:
// el perfil y los permisos los resuelve el servidor, no esta pantalla.
const ConsultaInformePenal = ({ showModal, setShowModal, idCaso }) => {
    const [loading, setLoading] = useState(false);
    const [informes, setInformes] = useState([]);
    const [detalle, setDetalle] = useState(null);
    const [error, setError] = useState('');
    // Cada petición lleva un número; al cerrar el modal o desmontar el componente el contador avanza y las respuestas tardías se descartan.
    const solicitud = useRef(0);

    useEffect(() => () => { solicitud.current += 1; }, []);

    const columnas = [
        { dataField: 'idInforme', text: 'ID Informe', sort: true },
        { dataField: 'nroVersion', text: 'Versión', sort: true },
        { dataField: 'fechaCreacion', text: 'Fecha Creación' },
        { dataField: 'glosaEstado', text: 'Estado', sort: true, formatter: (cell, row) => <InsigniaEstado estado={row.estado} glosa={cell} /> },
        { dataField: 'fechaEnvio', text: 'Fecha Envío', formatter: (cell) => cell || '—' },
        { dataField: 'usuarioEnvio', text: 'Usuario Envío', formatter: (cell, row) => row.nombreUsuarioEnvio || cell || '—' },
        { dataField: 'fechaRevision', text: 'Fecha Revisión', formatter: (cell) => cell || '—' },
        { dataField: 'usuarioRevision', text: 'Usuario Revisión', formatter: (cell, row) => row.nombreUsuarioRevision || cell || '—' },
        {
            dataField: 'opciones',
            text: 'Acción',
            isDummyField: true,
            formatter: (cell, row) => (
                <FaSearch
                    size={20}
                    title="Ver informe"
                    style={{ color: 'gray', cursor: 'pointer' }}
                    onClick={() => handleDetalle(row.idInforme)}
                />
            ),
            style: { textAlign: 'center', verticalAlign: 'middle' },
            headerStyle: { width: '8%' }
        }
    ].map((columna) => ({
        headerAlign: 'center',
        align: 'center',
        ...columna,
        // Después del spread: si la columna trae su propio headerStyle (p. ej. el ancho) se suma al color común y no lo pisa.
        headerStyle: { ...estiloEncabezado, ...(columna.headerStyle || {}) }
    }));

    useEffect(() => {
        if (showModal) getHistorial();
    }, [showModal])

    const getHistorial = async () => {
        const mia = ++solicitud.current;
        setLoading(true);
        setError('');
        try {
            const respuesta = await serviceGetInformePenal(codifica({ modo: 'historial', idCaso }));
            if (mia !== solicitud.current) return;
            setInformes(respuesta.informes || []);
        } catch (err) {
            if (mia !== solicitud.current) return;
            setError(err.message);
            setInformes([]);
        }
        setLoading(false);
    }

    const handleDetalle = async (idInforme) => {
        const mia = ++solicitud.current;
        setLoading(true);
        setError('');
        try {
            const respuesta = await serviceGetInformePenal(codifica({ modo: 'detalle', idCaso, idInforme }));
            if (mia !== solicitud.current) return;
            setDetalle(respuesta);
        } catch (err) {
            if (mia !== solicitud.current) return;
            setError(err.message);
        }
        setLoading(false);
    }

    // Desde el detalle «Volver» regresa a la grilla de la que se invocó; desde la grilla cierra el modal.
    const handleVolver = () => {
        if (detalle) {
            setDetalle(null);
        } else {
            handleClose();
        }
    }

    const handleClose = () => {
        solicitud.current += 1;
        setLoading(false);
        setShowModal(false);
        setDetalle(null);
        setInformes([]);
        setError('');
    }

    return (
        <Fragment>
            <LoadingComponent show={loading} />
            {/* `scrollable`: la cabecera y el pie («Volver») quedan fijos y solo se desplaza el cuerpo (el informe puede ser largo). */}
            <Modal show={showModal} onHide={handleClose} size='xl' scrollable>
                <Modal.Header className='cardHeader'>
                    <Modal.Title as='h6' className='mb-0'>
                        {detalle
                            ? <><BsFileEarmarkMedicalFill className='me-2' style={{ verticalAlign: '-2px' }} />{`Informe Defensa Penal — N° ${detalle.informe.idInforme} · Versión ${detalle.informe.nroVersion}`}</>
                            : 'HISTORIAL INFORME DEFENSA PENAL'}
                    </Modal.Title>
                </Modal.Header>

                {/* Datos del caso fijos bajo la cabecera (prototipo): solo en el detalle del informe. */}
                {detalle && detalle.caso && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px 20px', padding: '12px 18px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', fontSize: '0.82rem' }}>
                        <DatoCaso etiqueta='Nro. Siniestro' valor={detalle.caso.siniestro} />
                        <DatoCaso etiqueta='Fecha Siniestro' valor={detalle.caso.fechaSiniestro} />
                        <DatoCaso etiqueta='Placa Asegurado' valor={detalle.caso.placaAsegurado} />
                        <DatoCaso etiqueta='Nombre Asegurado' valor={detalle.caso.nombreAsegurado} />
                        <DatoCaso etiqueta='Abogado Inicial' valor={detalle.caso.abogadoInicial} />
                    </div>
                )}

                <Modal.Body>
                    {error ? <div className="alert alert-danger">{error}</div> : null}

                    {detalle
                        ? <InformePenalLectura datos={detalle} />
                        : (
                            <BootstrapTable
                                keyField="idInforme"
                                data={informes}
                                columns={columnas}
                                headerWrapperClasses="table-secondary"
                                pagination={paginationFactory({ sizePerPage: 5 })}
                                noDataIndication={<div className="text-center">No hay registros encontrados.</div>}
                            />
                        )}
                </Modal.Body>

                <Modal.Footer style={{ backgroundColor: '#f8f9fa' }}>
                    <Button style={{ width: '110px' }} variant='outline-info' onClick={handleVolver}>Volver</Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default ConsultaInformePenal
